import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,

} from "@mui/material";
import ListOrderIncomeExpenses from "./ListOrderIncomeExpenses";
import NewIncomeExpenseModal from "@/modules/finance/components/NewIncomeExpenseModal";
import { fetchOrderTransactions, clearOrderTransactions } from "../redux/OrderRedux";
import { createTransactionThunk, updateTransactionThunk, deleteTransactionThunk } from "@/modules/finance/redux/TransactionRedux";
import { uploadTransactionDocument } from "@/modules/documents/redux/DocumentCRUD";

// Component-specific interfaces
interface OrderIncomeExpensesProps {
  orderId?: string;
  contextCompanyId?: string;
}

const OrderIncomeExpenses: React.FC<OrderIncomeExpensesProps> = ({ orderId, contextCompanyId }) => {
  const dispatch = useDispatch();
  
  // Get data from Redux store
  const incomeExpenses = useSelector((state: any) => state.orders?.orderTransactions || []);
  const loading = useSelector((state: any) => state.orders?.transactionsLoading || false);
  const error = useSelector((state: any) => state.orders?.error || null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (orderId) {
      // Clear previous transactions
      dispatch(clearOrderTransactions());
      // Fetch transactions for this order
      dispatch(fetchOrderTransactions(orderId));
    }
  }, [orderId, dispatch]);

  const handleCreateIncomeExpense = async (incomeExpenseData: any) => {
    if (!orderId) {
      console.error("Không thể tạo thu chi: chưa có ID đơn hàng");
      return;
    }

    try {
      // Prepare transaction data
      const transactionData = {
        posting_date: incomeExpenseData.posting_date,
        document_date: incomeExpenseData.document_date,
        transaction_type: incomeExpenseData.transaction_type,
        actual_amount: incomeExpenseData.actual_amount,
        total_amount: incomeExpenseData.total_amount,
        wallet: incomeExpenseData.wallet,
        note: incomeExpenseData.note,
        order: orderId,
        customer: incomeExpenseData.customer,
        employee: incomeExpenseData.employee,
        company_id: incomeExpenseData.company_id || contextCompanyId || undefined,
        company: incomeExpenseData.company || incomeExpenseData.company_id || contextCompanyId || undefined,
        debt_type: incomeExpenseData.debt_type,
        status: incomeExpenseData.status
      };

      // Create transaction via API
      const result = await dispatch(createTransactionThunk(transactionData));
      
      if (createTransactionThunk.fulfilled.match(result)) {
        const createdTransaction = result.payload?.transaction || result.payload;
        const transactionId = createdTransaction?.id;

        // Upload files if any
        const selectedFiles = incomeExpenseData.selectedFiles || [];
        if (transactionId && selectedFiles.length > 0) {
          for (const file of selectedFiles) {
            try {
              await uploadTransactionDocument(transactionId, file);
            } catch (fileError) {
              console.error(`Failed to upload file ${(file as File).name}:`, fileError);
            }
          }
        }
        
        setCreateDialogOpen(false);
        
        // Refresh transactions after creating
        dispatch(fetchOrderTransactions(orderId));
      } else {
        console.error("Failed to create transaction:", result);
      }
    } catch (err: any) {
      console.error("Không thể tạo thu chi:", err);
    }
  };

  const handleEditIncomeExpense = async (incomeExpenseId: string, updateData: any) => {
    try {
      // Prepare update data (exclude selectedFiles from transaction update)
      const { selectedFiles, ...transactionData } = updateData;
      const transactionUpdateData = {
        id: incomeExpenseId,
        ...transactionData
      };

      // Update transaction via API
      const result = await dispatch(updateTransactionThunk(transactionUpdateData));
      
      if (updateTransactionThunk.fulfilled.match(result)) {
        // Upload files if any
        if (selectedFiles && selectedFiles.length > 0) {
          for (const file of selectedFiles) {
            try {
              await uploadTransactionDocument(incomeExpenseId, file);
            } catch (fileError) {
              console.error(`Failed to upload file ${file.name}:`, fileError);
            }
          }
        }
        
        // Refresh transactions after editing
        if (orderId) {
          await dispatch(fetchOrderTransactions(orderId));
        }
      } else {
        console.error("Failed to update transaction:", result);
      }
    } catch (err: any) {
      console.error("Không thể cập nhật thu chi:", err);
    }
  };

  const handleDeleteIncomeExpense = async (incomeExpenseId: string) => {
    try {
      // Delete transaction via API
      const result = await dispatch(deleteTransactionThunk(incomeExpenseId));
      
      if (deleteTransactionThunk.fulfilled.match(result)) {
        // Refresh transactions after deleting
        if (orderId) {
          dispatch(fetchOrderTransactions(orderId));
        }
      } else {
        console.error("Failed to delete transaction:", result);
      }
    } catch (err: any) {
      console.error("Không thể xóa thu chi:", err);
    }
  };

  // Show message when no orderId (creating new order)
  if (!orderId) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Quản lý thu chi
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Lưu đơn hàng để có thể tạo và quản lý thu chi
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      <ListOrderIncomeExpenses
        incomeExpenses={incomeExpenses}
        loading={loading}
        onCreateIncomeExpense={() => setCreateDialogOpen(true)}
        onEditIncomeExpense={handleEditIncomeExpense}
        onDeleteIncomeExpense={handleDeleteIncomeExpense}
        error={error}
        onClearError={() => dispatch({ type: 'orders/clearError' })}
      />

      <NewIncomeExpenseModal
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateIncomeExpense}
        orderId={orderId}
        contextCompanyId={contextCompanyId}
      />
    </Box>
  );
};

export default OrderIncomeExpenses;
